import SockJS from 'sockjs-client'
import { Client } from '@stomp/stompjs'
import store from '@/store'
import order from '@/apis/order'
import router from '@/router'
import { emitter } from '@/utils/mitt'
import { alert as $alert } from '@/utils/alert'
import storeManager from '@/utils/storeManager'
import { api } from '@/apis/api'

let _visibilityHandler = null

export default {
	namespaced: true,
	// modules: {}
	state: {
		isSocketInitialized: false,
		stompClient: null,
		heartbeatTimer: null, // 心跳計時器
		errorTimeout: null, // 斷線重連計時器
		isConnected: false,
		isConnecting: false, // 是否正在建立連線，避免併發
		onWsWatchOrder: null, // {orderStatusId: number, orderId: number}
		errorCount: 0, // 斷線重連次數
		// 重連成功序號：後端不補推斷線期間的事件、STOMP 重訂閱也只收之後的推播，
		// 故由各頁／App watch 此值後自行重同步（節流由各消費端負責）
		reconnectSeq: 0,
		// 由所有 reconnection 入口統一設置；不能用 errorCount 推斷，因心跳失敗與
		// 服務端 m=1 會直接重連、沒有先增加錯誤次數。
		pendingResync: false,
	},
	mutations: {
		setOnWsWatchOrder($, id) {
			$.onWsWatchOrder = id
		},
		bumpReconnectSeq($) {
			$.reconnectSeq++
		},
	},
	actions: {
		initWebSocket(ctx) {
			const { dispatch, state } = ctx
			state.isConnected = true
			// 先清理舊的心跳定時器（如果有）
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}
			dispatch('setupVisibilityHandler')
			dispatch('connection')
		},
		startHeartbeat(ctx) {
			const { dispatch, state } = ctx
			// 清理可能存在的舊定時器
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}
			// 連線正常時每40秒心跳檢測
			state.heartbeatTimer = setInterval(() => {
				try {
					// 使用新的 publish 方法
					state.stompClient.publish({
						destination: '/app/connect/shop',
						headers: {},
						body: 'PING',
					})
				} catch (err) {
					dispatch('reconnection')
				}
			}, 40000)
		},
		connection(ctx) {
			const { state, dispatch } = ctx
			if (!state.isConnected) {
				return
			}
			// 防止併發連線
			if (state.isConnecting) {
				$devLog(
					'%cWebSocket 連線進行中，略過',
					'color:orange;background-color:#fff;',
				)
				return
			}
			// 已連線則略過
			if (state.stompClient && state.stompClient.connected) {
				$devLog('%cWebSocket 已連線，略過', 'color:blue;background-color:#fff;')
				return
			}
			// 只有在連線已清理的情況下才建立新連線
			if (!state.isSocketInitialized && state.stompClient == null) {
				const wsToken = store.state.user.wsToken
				const storeId = storeManager.storeId
				const qrCodeHash = storeManager.qrCodeHash

				const params = new URLSearchParams()
				if (wsToken) {
					params.append('wsToken', wsToken)
				}
				if (storeId) {
					params.append('storeId', storeId)
				}
				if (qrCodeHash) {
					params.append('qrCodeHash', qrCodeHash)
				}

				const wsUrl = `${
					process.env.VUE_APP_BASE_API
				}/app/websocket?${params.toString()}`

				// 使用新的 @stomp/stompjs API
				state.stompClient = new Client({
					webSocketFactory: () =>
						new SockJS(wsUrl, null, { transports: ['websocket'] }),
					reconnectDelay: 0, // 停用內建自動重連，統一由 onErrorReConnection 管理
					debug: () => {},
					onConnect: () => {
						state.isConnecting = false
						dispatch('successCallback')
					},
					onStompError: frame => {
						const msg = frame.headers?.message || '未知錯誤'
						console.error(`[WebSocket] STOMP ERROR: ${msg}`, frame.body || '')
						state.isConnecting = false
						dispatch('onErrorReConnection')
					},
					onWebSocketClose: () => {
						// 僅意外斷線才觸發重連；主動 deactivate() 時 isConnected 已為 false，不會進入
						if (state.isConnected) {
							state.isConnecting = false
							dispatch('onErrorReConnection')
						}
					},
				})
				state.isSocketInitialized = true
			}

			// 啟動連線
			if (state.stompClient && !state.stompClient.active) {
				state.isConnecting = true
				state.stompClient.activate()
			}
		},
		successCallback(ctx) {
			const { state, dispatch } = ctx
			const shouldResync = state.pendingResync
			state.pendingResync = false
			state.errorCount = 0
			if (state.errorTimeout) {
				clearTimeout(state.errorTimeout)
				state.errorTimeout = null
			}
			$devLog('%c連接成功', 'color:green;background-color:#fff;')
			// 連接成功後才啟動心跳定時器
			dispatch('startHeartbeat')
			if (shouldResync) {
				// 由各頁／App watch reconnectSeq 自行重同步（頁面補刷需自行節流）
				ctx.commit('bumpReconnectSeq')
			}
			if (!store.getters.isKiosk) {
				state.stompClient.subscribe('/user/store', ({ body }) => {
					const JSONBody = JSON.parse(body)
					$devLog('/user/store', JSONBody)
					store.commit('takeMeal/SET_TAKE_NUMBER', JSONBody.maxTakeNumber)
					store.commit(
						'takeMeal/SET_TAKE_NUMBER_LIST',
						JSONBody.canTakeNumberList,
					)
				})
			}
			state.stompClient.subscribe('/user/subscribe', ({ body }) => {
				const JSONBody = JSON.parse(body)
				const { paidStatus, id, status, storeId } = JSONBody
				if (!storeId) {
					const shouldReconnect = JSONBody.m === '1' || JSONBody.m === 1
					if (shouldReconnect) {
						$devLog(
							'%c接收到異常通知，重新建立連線',
							'color:red;background-color:#fff;',
							JSONBody,
						)
						dispatch('reconnection')
					}
					return
				}
				if (store.getters.isKiosk) {
					return
				}

				$devLog('/user/subscribe', JSONBody)
				const save = {
					orderId: id,
					orderStatus: status,
				}

				if (router.history.current.name === 'TakeMeal')
					emitter.emit('TakeMeal_PAID', { id, paidStatus })

				if (router.history.current.name !== 'TakeMeal' && status === 3) {
					router.push(`/${router.history.current.params.random}/TakeMeal/${id}`)
				} else if (status === 5 || status === 6) {
					store.state.isCancelOrderDialog = true
					ctx.commit('setOnWsWatchOrder', save)
				} else {
					ctx.commit('setOnWsWatchOrder', save)
				}
			})
			// 訂閱刷新通道
			const storeId = storeManager.storeId
			if (storeId) {
				state.stompClient.subscribe(`/refresh/${storeId}`, async msg => {
					const data = JSON.parse(msg.body)
					$devLog('/refresh', data)
					const currStoreId = storeManager.storeId
					if (currStoreId !== data.storeId) {
						$devLog('店家id不合')
						return
					}
					if (data.type === 'SETTING') {
						$devLog('刷新店家設定')
						ctx.commit('store/SET_REFRESH_SETTING', true, { root: true })
					}

					if (data.type === 'MENU') {
						// 版本號無條件遞增：事件發生在 Cart / Detail 期間時不會即時補刷，
						// 但返回菜單頁時要靠版本差異判定五分鐘快取已失效
						ctx.commit('store/BUMP_MENU_CHANGE_SEQ', null, { root: true })
						// 當前在pos頁才刷新
						if (router.history.current.name === 'Store') {
							$devLog('刷新菜單')
							ctx.commit('store/SET_REFRESH_MENU', true, { root: true })
						}
					}

					if (data.type === 'AUTOMATIC_DISCOUNT') {
						// 需要重取資料，標記為未就緒
						ctx.commit('store/SET_AUTOMATIC_DISCOUNT_LIST_READY', false, {
							root: true,
						})
						ctx.commit('store/SET_REFRESH_AUTOMATIC_DISCOUNT', true, {
							root: true,
						})
					}
				})
			}
			// 訂閱平台公告通道
			state.stompClient.subscribe(`/announcement/platform`, async msg => {
				const data = JSON.parse(msg.body)
				$devLog('/announcement/platform', data)
				if (data.event === 'START_MAINTENANCE') {
					ctx.commit(
						'setMaintenance',
						{
							modal: true,
							title: data.title,
							message: data.message,
						},
						{ root: true },
					)
					return
				}

				if (data.event === 'END_MAINTENANCE') {
					ctx.commit(
						'setMaintenance',
						{
							modal: false,
							title: '',
							message: '',
						},
						{ root: true },
					)
				}
			})
		},
		onErrorReConnection(ctx) {
			const { dispatch, state, rootState } = ctx
			if (rootState.maintenance.modal) {
				state.errorCount = 1
			}
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}

			state.isConnected = false
			if (state.errorCount < 15) {
				state.errorCount++
				const now = new Date()
				const formattedTime = new Intl.DateTimeFormat('zh-TW', {
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false,
				}).format(now)

				$devLog(
					`%c斷線續連嘗試次數 ${state.errorCount} - 當前時間: ${formattedTime}`,
					'color:red;background-color:#fff;',
				)

				if (state.errorTimeout) {
					clearTimeout(state.errorTimeout)
					state.errorTimeout = null
				}
				if (state.errorCount % 5 === 0) {
					$alert('您的網路不太穩定!', 'danger')
				}
				const backoffCeil = Math.min(
					12000,
					3000 * 2 ** Math.max(0, state.errorCount - 1),
				)
				const reconnectDelay = Math.round(
					backoffCeil / 2 + Math.random() * (backoffCeil / 2),
				)
				state.errorTimeout = setTimeout(
					() => dispatch('reconnection'),
					reconnectDelay,
				)
				return
			}

			$devLog(
				`%c多次重連失敗，重新整理頁面`,
				'color:red;background-color:#fff;',
			)
			dispatch('disconnect')
			state.errorCount = 0
			window.location.reload()
		},
		setupVisibilityHandler(ctx) {
			const { dispatch, state } = ctx
			if (_visibilityHandler) return

			_visibilityHandler = () => {
				if (document.visibilityState !== 'visible') return

				// 判斷是否「本來就預期要連線」
				const wasActive = state.isSocketInitialized || state.errorCount > 0
				const isConnected = state.stompClient && state.stompClient.connected

				if (!wasActive || isConnected) return

				// 取消 onErrorReConnection 的 3 秒延遲，立即重連
				if (state.errorTimeout) {
					clearTimeout(state.errorTimeout)
					state.errorTimeout = null
				}

				$devLog(
					'%c[Visibility] 分頁切回，偵測到斷線，立即重連',
					'color:#FF5722;',
				)
				dispatch('reconnection')
			}

			document.addEventListener('visibilitychange', _visibilityHandler)
		},
		async reconnection(ctx) {
			const { dispatch, state } = ctx
			state.pendingResync = true
			await dispatch('disconnect')
			// 首次重連或每 5 次重試時刷新 wsToken
			if (state.errorCount <= 1 || state.errorCount % 5 === 0) {
				try {
					const res = await api.user.getWsToken()
					store.commit('user/SET_WS_TOKEN', res.data)
				} catch (e) {
					$devLog(
						'%c[WebSocket] 重連時刷新 wsToken 失敗，使用舊 token',
						'color:orange;',
					)
				}
			}
			dispatch('initWebSocket')
		},
		async disconnect(ctx) {
			const { state } = ctx
			state.isConnected = false
			state.isConnecting = false
			// 清理定時器
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}
			if (state.errorTimeout) {
				clearTimeout(state.errorTimeout)
				state.errorTimeout = null
			}
			// 斷開連線 - 使用新的 deactivate 方法
			if (state.stompClient !== null) {
				await state.stompClient.deactivate()
			}
			state.isSocketInitialized = false
			state.stompClient = null
		},
		async addOrder(ctx, data) {
			return await order.addOrder(data)
		},
	},
}
